import { useState, useMemo } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { useCompany } from "../components/CompanySelector";
import LoadingSpinner from "../components/LoadingSpinner";
import ReportTable from "../components/ReportTable";
import { OutstandingReceivableItem } from "../dto/receivable.dto";
import { useAccounts, type Account } from "../hooks/use-accounts";
import { getOutstandingReceivables } from "../services/receivables.service";
import { formatCurrency } from "../utils/helpers";
import * as XLSX from "xlsx";

const OutstandingReceivablePage: React.FC = () => {
  const { selectedCompany } = useCompany();
  const { accounts } = useAccounts(selectedCompany?.id);

  const allowedReconciledAccounts = useMemo(() => {
    return accounts.filter((act) => act.reconcile);
  }, [accounts]);

  const [date, setDate] = useState<Date | null>(new Date());
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  const [receivableDebits, setReceivableDebits] = useState<
    OutstandingReceivableItem[] | undefined
  >(undefined);
  const [receivableCredits, setReceivableCredits] = useState<
    OutstandingReceivableItem[] | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const debitColumns = [
    { header: "Name", accessor: "name" },
    { header: "Date", accessor: "date" },
    { header: "Partner", accessor: "partnerName" },
    {
      header: "Debit",
      accessor: (row: OutstandingReceivableItem) => formatCurrency(row.debit),
    },
    {
      header: "Reconciled Amount",
      accessor: (row: OutstandingReceivableItem) =>
        formatCurrency(row.reconciledAmount),
    },
    {
      header: "Residual Amount",
      accessor: (row: OutstandingReceivableItem) =>
        formatCurrency(row.residualAmount),
    },
  ];

  const creditColumns = [
    { header: "Name", accessor: "name" },
    { header: "Date", accessor: "date" },
    { header: "Partner", accessor: "partnerName" },
    {
      header: "Credit",
      accessor: (row: OutstandingReceivableItem) => formatCurrency(row.credit),
    },
    {
      header: "Reconciled Amount",
      accessor: (row: OutstandingReceivableItem) =>
        formatCurrency(row.reconciledAmount),
    },
    {
      header: "Residual Amount",
      accessor: (row: OutstandingReceivableItem) =>
        formatCurrency(row.residualAmount),
    },
  ];

  const debitRecidualAmountTotal = useMemo(
    () =>
      receivableDebits
        ? receivableDebits.reduce((acc, val) => (acc += val.residualAmount), 0)
        : 0,
    [receivableDebits],
  );

  const creditRecidualAmountTotal = useMemo(
    () =>
      receivableCredits
        ? receivableCredits.reduce((acc, val) => (acc += val.residualAmount), 0)
        : 0,
    [receivableCredits],
  );

  const generateOutstandingReceivables = async () => {
    if (date && selectedAccount) {
      try {
        setLoading(true);
        const dateStr = date?.toISOString().split("T")[0];
        let [debits, credits] = await Promise.all([
          getOutstandingReceivables({
            type: "debit",
            accountId: selectedAccount?.id,
            date: dateStr,
          }),
          getOutstandingReceivables({
            type: "credit",
            accountId: selectedAccount?.id,
            date: dateStr,
          }),
        ]);

        debits = debits.filter((d) => d.residualAmount !== 0);
        credits = credits.filter((c) => c.residualAmount !== 0);

        setReceivableDebits(debits);
        setReceivableCredits(credits);
      } catch (error) {
        console.log(error);
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    }
  };

  const exportDebitData = () => {
    exportExcel(
      `outstanding_receivable_debit_${selectedAccount?.name}_${date?.toISOString().split("T")[0]}`,
      receivableDebits || [],
    );
  };

  const exportCreditData = () => {
    exportExcel(
      `outstanding_receivable_credit_${selectedAccount?.name}_${date?.toISOString().split("T")[0]}`,
      receivableCredits || [],
    );
  };

  const exportExcel = (name: string, data: Record<string, any>[]) => {
    const ws = XLSX.utils.json_to_sheet(data);

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    // Download Excel file
    XLSX.writeFile(wb, `${name}.xlsx`);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">
        Outstanding Reconciliable Account
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6 items-start">
        <div className="col-span-12 md:col-span-2">
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            Date
          </label>
          <DatePicker
            selected={date}
            onChange={(date: Date | null) => setDate(date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>

        <div className="col-span-12 md:col-span-4">
          <label
            htmlFor="accountSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Accounts:
          </label>
          <Select
            id="accountSelector"
            placeholder="Select Account"
            value={selectedAccount}
            options={allowedReconciledAccounts}
            getOptionLabel={(option) => `${option.code} - ${option.name}`}
            getOptionValue={(option) => `${option.id}`}
            onChange={(selectedOption) => setSelectedAccount(selectedOption)}
            className="mt-1"
          />
        </div>

        <div className="col-span-12 md:col-span-6 md:mt-5">
          <button
            type="button"
            onClick={generateOutstandingReceivables}
            className="disabled:cursor-not-allowed w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            disabled={!selectedAccount || !date || loading}
          >
            {loading ? "Generating..." : "Generate"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="flex flex-col justify-center items-center">
          <p>Error, Coba lagi</p>
          <button type="button" onClick={generateOutstandingReceivables}>
            Muat Ulang
          </button>
        </div>
      ) : receivableCredits && receivableDebits ? (
        <>
          <div className="flex items-center justify-center">
            <div className="inline-block">
              Balance:{" "}
              {formatCurrency(
                debitRecidualAmountTotal - creditRecidualAmountTotal,
              )}
            </div>
          </div>

          <div className="mb-6 border-b border-gray-200 pb-8 space-y-2 ">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Debit</h3>
              <div className="inline-block">
                Total Residual: {formatCurrency(debitRecidualAmountTotal)}
              </div>
            </div>

            {receivableDebits && (
              <ReportTable columns={debitColumns} data={receivableDebits} />
            )}

            <div className="flex justify-end mb-4">
              <button
                onClick={exportDebitData}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Export to XLSX
              </button>
            </div>
          </div>

          <div className="py-2 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Credit</h3>
              <div className="inline-block">
                Total Residual: {formatCurrency(creditRecidualAmountTotal)}
              </div>
            </div>
            {receivableCredits && (
              <ReportTable columns={creditColumns} data={receivableCredits} />
            )}

            <div className="flex justify-end mb-4">
              <button
                onClick={exportCreditData}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Export to XLSX
              </button>
            </div>
          </div>
        </>
      ) : (
        <p className="text-gray-500 text-center">
          No data available for the selected criteria. Please generate the
          report.
        </p>
      )}
    </div>
  );
};

export default OutstandingReceivablePage;
